import { EnvelopeSimpleIcon } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { SoftPromptCard } from "@/client/components/soft-prompt-card";
import { toast } from "@/client/hooks/use-toast";
import {
  recordEmailNudgeAccepted,
  recordEmailNudgeClosed,
  recordEmailNudgeDeclined,
  recordEmailNudgeLater,
  recordEmailNudgeShown,
  shouldShowEmailNudge,
  hasReachedEmailNudgeJ1,
  EMAIL_NUDGE_DURATION_MS,
  EMAIL_NUDGE_FIRST_DELAY_MS,
} from "@/client/services/notification/email-nudge";
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
} from "@/client/services/notification";
import { useUser } from "@/client/services/user";

import { NOTIFICATION_CATEGORIES, type NotificationCategory } from "@reactive-resume/dto";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  E-MAIL OPT-IN NUDGE — toast d'activation du canal e-mail (J+1)
 * ─────────────────────────────────────────────────────────────────────────────
 *  3e toast d'activation (après push et PWA), monté une seule fois dans le
 *  provider global à côté de `PushOptInNudge`.
 *
 *  Règles web.dev : jamais d'affichage au chargement (le timer ne s'arme
 *  qu'après une interaction réelle, déclencheur J+1), pré-contexte avant
 *  l'action, refus mémorisé (re-proposition sur changement de relation).
 *
 *  Effet de bord : l'activation écrit la préférence `email` de chaque
 *  catégorie → la matrice Paramètres → Notifications reflète l'opt-in.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const EmailOptInNudge = () => {
  const { user } = useUser();
  const location = useLocation();
  const { data: preferences } = useNotificationPreferences();
  const updatePreference = useUpdateNotificationPreference();

  // Verrou de session : une seule décision (affichée ou non) par session.
  const settledRef = useRef(false);

  // Dès que la préférence e-mail est active quelque part, l'opt-in est acquis.
  useEffect(() => {
    if (!preferences) return;
    if (preferences.some((preference) => preference.email)) {
      recordEmailNudgeAccepted();
    }
  }, [preferences]);

  useEffect(() => {
    const pathname = location.pathname;
    const inClientSpace =
      pathname.startsWith("/client") ||
      pathname.startsWith("/builder") ||
      pathname.startsWith("/artboard");

    if (settledRef.current) return;
    if (!user?.id) return;
    if (!inClientSpace) return;
    if (!hasReachedEmailNudgeJ1()) return;
    if (!shouldShowEmailNudge()) return;
    if (preferences?.some((preference) => preference.email)) return;

    settledRef.current = true;

    // Règle web.dev : le timer ne s'arme qu'après une interaction réelle.
    let timer: number | null = null;

    const arm = () => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        // L'impression n'est comptée qu'au moment réel de l'affichage.
        recordEmailNudgeShown();

        const toastId = toast.custom(
          (id) => (
            <SoftPromptCard
              toastId={id}
              icon={<EnvelopeSimpleIcon size={22} weight="duotone" />}
              title="Recevez vos rappels directement par e-mail"
              description="Offres de fidélité, parrainage et conseils pour votre recherche : nous vous écrivons seulement quand cela compte."
              actionLabel="Activer les e-mails"
              laterLabel="Plus tard"
              declineLabel="Non merci"
              onAction={async () => {
                try {
                  await Promise.all(
                    NOTIFICATION_CATEGORIES.map((category: NotificationCategory) =>
                      updatePreference.mutateAsync({
                        category,
                        data: { email: true },
                      }),
                    ),
                  );
                  recordEmailNudgeAccepted();
                  toast.success("E-mails activés", {
                    description:
                      "Vous recevrez désormais vos rappels et offres importantes par e-mail.",
                    duration: 5_000,
                  });
                } catch {
                  // Échec transitoire : on retentera selon la cadence usuelle.
                  recordEmailNudgeClosed();
                  toast.error("Activation impossible", {
                    description:
                      "Réessayez dans quelques instants ou activez le canal e-mail depuis Paramètres → Notifications.",
                    duration: 8_000,
                  });
                }
              }}
              onLater={() => {
                recordEmailNudgeLater();
                toast.dismiss(id);
              }}
              onDecline={() => {
                recordEmailNudgeDeclined();
                toast.dismiss(id);
              }}
              onClose={() => {
                recordEmailNudgeClosed();
                toast.dismiss(id);
              }}
            />
          ),
          { unstyled: true, duration: EMAIL_NUDGE_DURATION_MS },
        );

        // Sécurité : la carte disparaît seule si l'utilisateur n'agit pas.
        window.setTimeout(() => toast.dismiss(toastId), EMAIL_NUDGE_DURATION_MS);
      }, EMAIL_NUDGE_FIRST_DELAY_MS);
    };

    window.addEventListener("pointerdown", arm, { once: true, capture: true });
    window.addEventListener("keydown", arm, { once: true, capture: true });

    return () => {
      window.removeEventListener("pointerdown", arm, { capture: true });
      window.removeEventListener("keydown", arm, { capture: true });
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [user?.id, location.pathname, preferences, updatePreference]);

  return null;
};

